/**
 * Deterministic trigonometry. Math.sin, Math.cos, and Math.atan2 may differ in
 * their last bits between JavaScript engines, so the simulation uses these
 * polynomial approximations, which only use addition, multiplication,
 * division, floor, and sqrt (all of which are IEEE-754 exact). Accuracy is
 * better than 1e-6 over the full range, checked by test/fastmath.test.ts.
 */

export const PI = Math.PI;
export const TWO_PI = 6.283185307179586;
export const HALF_PI = 1.5707963267948966;
const INV_TWO_PI = 1 / TWO_PI;
const SIXTH_PI = PI / 6;
const SQRT3 = 1.7320508075688772;
const TAN_PI_12 = 0.2679491924311227;
export const DEG = PI / 180;
export const RAD_TO_DEG = 180 / PI;

/** Reduce an angle to [-PI, PI]. */
export function wrapAngle(x: number): number {
  return x - TWO_PI * Math.floor((x + PI) * INV_TWO_PI);
}

/** Taylor series on [-PI/2, PI/2], terms through x^13. Max error ~1e-9. */
function sinCore(x: number): number {
  const x2 = x * x;
  return (
    x *
    (1 +
      x2 *
        (-1 / 6 +
          x2 *
            (1 / 120 + x2 * (-1 / 5040 + x2 * (1 / 362880 + x2 * (-1 / 39916800 + x2 * (1 / 6227020800)))))))
  );
}

export function sin(x: number): number {
  let r = wrapAngle(x);
  if (r > HALF_PI) r = PI - r;
  else if (r < -HALF_PI) r = -PI - r;
  return sinCore(r);
}

export function cos(x: number): number {
  return sin(x + HALF_PI);
}

/** atan on [0, TAN_PI_12] via Taylor series through x^13. */
function atanSmall(x: number): number {
  const x2 = x * x;
  return (
    x * (1 + x2 * (-1 / 3 + x2 * (1 / 5 + x2 * (-1 / 7 + x2 * (1 / 9 + x2 * (-1 / 11 + x2 * (1 / 13)))))))
  );
}

/** atan for x in [0, 1]. */
function atanUnit(x: number): number {
  if (x > TAN_PI_12) {
    return SIXTH_PI + atanSmall((SQRT3 * x - 1) / (SQRT3 + x));
  }
  return atanSmall(x);
}

export function atan(x: number): number {
  const ax = x < 0 ? -x : x;
  const r = ax > 1 ? HALF_PI - atanUnit(1 / ax) : atanUnit(ax);
  return x < 0 ? -r : r;
}

export function atan2(y: number, x: number): number {
  if (x > 0) return atan(y / x);
  if (x < 0) {
    if (y >= 0) return atan(y / x) + PI;
    return atan(y / x) - PI;
  }
  if (y > 0) return HALF_PI;
  if (y < 0) return -HALF_PI;
  return 0;
}

export function sqrt(x: number): number {
  return Math.sqrt(x);
}

export function hypot(x: number, y: number): number {
  return Math.sqrt(x * x + y * y);
}

export function clamp(x: number, lo: number, hi: number): number {
  return x < lo ? lo : x > hi ? hi : x;
}

export function sign(x: number): number {
  return x > 0 ? 1 : x < 0 ? -1 : 0;
}

export function abs(x: number): number {
  return x < 0 ? -x : x;
}

/** Move `x` toward `target` by at most `maxDelta`. */
export function moveToward(x: number, target: number, maxDelta: number): number {
  const d = target - x;
  if (d > maxDelta) return x + maxDelta;
  if (d < -maxDelta) return x - maxDelta;
  return target;
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}
